import { ContractMessage } from '../../../../types/ContractMessage';
import { VerticalDetail } from '../../../common/Detail/VerticalDetail';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { formatNumber } from '../../../common/helpers/formatNumber.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { Unit } from '../../../common/models/ItemModel';
import { Box } from '../../../common/ui/Box';
import { DurationLeftDisplay } from '../../../contracts/detail/components/DurationLeftDisplay';

const Spacer = () => {
    return (
        <>
            {Characters.EN_SPACE}
            {Characters.MIDDLE_DOT}
            {Characters.EN_SPACE}
        </>
    );
};

const getUserDisplayName = (firstname?: string, lastname?: string) => {
    const fullName = [firstname, lastname].filter(Boolean).join(' ').trim();
    return fullName || undefined;
};

interface ContractDetailsSectionProps {
    contract: ContractMessage;
}

export const ContractDetailsSection = ({ contract }: ContractDetailsSectionProps) => {
    const companyNames = contract.customers
        .map((customer) => formatCompanyName({ company: customer }))
        .filter((companyName): companyName is string => !!companyName)
        .join(', ');
    const excludedVessels = contract.excludedVessels ?? [];
    const excludedVesselCount = excludedVessels.length;
    const excludedVesselNames = excludedVessels.map((vessel) => vessel.name).join(', ');

    return (
        <Box display="flex" flexDirection="column" gap={12}>
            <VerticalDetail
                label={translate('contracts.detail.minimumVolume')}
                value={
                    contract.minimumVolume !== undefined && contract.minimumVolume !== null ? (
                        <>
                            {formatNumber({
                                number: contract.minimumVolume,
                                maximumFractionDigits: 0,
                            })}
                            {Characters.NO_BREAK_SPACE}
                            {Unit.L}
                        </>
                    ) : undefined
                }
            />
            <DurationLeftDisplay validUntil={contract.validUntil} />
            <VerticalDetail
                label={translate('contracts.detail.created')}
                value={
                    contract.dateCreated ? (
                        <>
                            {formatDate({ date: contract.dateCreated })}
                            {getUserDisplayName(contract.createdByUser?.firstname, contract.createdByUser?.lastname) ? (
                                <>
                                    <Spacer />
                                    {getUserDisplayName(
                                        contract.createdByUser?.firstname,
                                        contract.createdByUser?.lastname
                                    )}
                                </>
                            ) : null}
                        </>
                    ) : null
                }
            />
            <VerticalDetail
                label={translate('contracts.labels.lastModified')}
                value={
                    contract.dateUpdated ? (
                        <>
                            {formatDate({ date: contract.dateUpdated })}
                            {getUserDisplayName(contract.updatedByUser?.firstname, contract.updatedByUser?.lastname) ? (
                                <>
                                    <Spacer />
                                    {getUserDisplayName(
                                        contract.updatedByUser?.firstname,
                                        contract.updatedByUser?.lastname
                                    )}
                                </>
                            ) : null}
                        </>
                    ) : null
                }
            />
            {companyNames ? (
                <VerticalDetail label={translate('contracts.detail.companies')} value={companyNames} />
            ) : null}
            {excludedVesselCount ? (
                <VerticalDetail
                    label={translate('contracts.vesselAccess.excludedVesselsCount', {
                        count: excludedVesselCount.toString(),
                    })}
                    value={excludedVesselNames || null}
                />
            ) : null}
        </Box>
    );
};
